import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  IconProps,
  InputLeftElementProps,
  InputProps,
  useColorModeValue,
  useStyleConfig,
} from "@chakra-ui/react";
import Checkbox from "components/checkbox";
import { CopyButton } from "components/copy-button";
import { InputFieldWithError } from "components/ui/input-field";
import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { createEventListener } from "utils/events";
import { filterArrayBy } from "utils/filter-array";
import { registerPortal, unregisterPortal } from "utils/portal-registry";
import { getRelativeRect } from "utils/relative-rect";

export interface InputSearchProps {
  options: any[];
  isActiveOption: (option: any) => boolean;
  onChange: (value: any) => void;
  inputProps?: InputProps;
  isLoading?: boolean;
  optionValue?: string;
  optionLabel?: string;
  optionLabelRender?: (option: any) => any;
  searchKeys?: string[];
  errorMessage?: string;
  clearInput?: boolean;
  openOnFocus?: boolean;
  value?: string;
  sortBy?: string;
  resetOption?: boolean;
  selectedOption?: any;
  multiSelect?: boolean;
  selectedFirst?: boolean;
  isOpenInFirst?: boolean;
  openBoxProps?: BoxProps;
  mainBoxProps?: BoxProps;
  leftIcon?: JSX.Element;
  leftIconProps?: InputLeftElementProps;
  rightIconProps?: IconProps;
  disableOnClickSelect?: boolean;
  optionsFlexProps?: FlexProps;
  limitRender?: number;
  enableCopyOption?: boolean;
  isFilter?: boolean;
}

export function InputSearch({
  options = [],
  onChange,
  inputProps,
  isLoading,
  searchKeys = ["name"],
  optionLabel = "name",
  optionValue = "value",
  errorMessage,
  isActiveOption,
  optionLabelRender,
  clearInput = true,
  openOnFocus,
  sortBy,
  value = "",
  resetOption = false,
  selectedOption,
  multiSelect,
  selectedFirst = true,
  isOpenInFirst = false,
  openBoxProps,
  mainBoxProps,
  leftIcon,
  leftIconProps,
  rightIconProps,
  disableOnClickSelect = false,
  optionsFlexProps,
  limitRender = 20,
  enableCopyOption = false,
  isFilter = false,
}: InputSearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [result, setResult] = useState<any[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [portalPosition, setPortalPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const partnerListRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const isOpened =
    isOpenInFirst === false
      ? isOpen && (openOnFocus || result.length)
      : isOpenInFirst;
  const cardStyles = useStyleConfig("Card") as BoxProps;
  const bg = cardStyles.bg as string;
  const border = cardStyles.border as string;
  // const borderRadius = cardStyles.borderRadius as string;
  const activeColor = useColorModeValue("gray.200", "rgba(255,255,255, .1)");
  const hoverColor = useColorModeValue(
    "var(--chakra-colors-gray-100)",
    "var(--chakra-colors-whiteAlpha-100)",
  );
  const selecioneOption = {
    [optionLabel]: "Selecione",
    [optionValue]: undefined,
  };
  const [searchValue, setSearchValue] = useState("");
  // Opções selecionadas no modo filtro (isFilter) aguardando o debounce
  const [pendingOptions, setPendingOptions] = useState<any[]>([]);
  const pendingOptionsRef = useRef<any[]>([]);
  const filterDebounceRef = useRef<NodeJS.Timeout | null>(null);
  if (multiSelect) resetOption = false;

  const id = useId();

  // Dispara as mudanças pendentes do modo filtro após 1s sem novas seleções
  const flushPendingChanges = () => {
    filterDebounceRef.current = null;
    const pending = pendingOptionsRef.current;
    if (!pending.length) return;
    pendingOptionsRef.current = [];
    setPendingOptions([]);
    // No multiSelect cada clique precisa ser notificado para aplicar o toggle;
    // no singleSelect apenas a última seleção importa.
    if (multiSelect) pending.forEach((option) => onChange(option));
    else onChange(pending[pending.length - 1]);
  };

  const selectOption = (option: any) => {
    if (isFilter) {
      // Atualiza visualmente na hora, mas só chama a mudança após 1s
      // sem novas seleções (debounce).
      pendingOptionsRef.current = [...pendingOptionsRef.current, option];
      setPendingOptions(pendingOptionsRef.current);
      if (!multiSelect) {
        setSearchValue(option?.[optionLabel] ?? "");
        setIsOpen(false);
      }
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
      filterDebounceRef.current = setTimeout(flushPendingChanges, 1000);
      return;
    }
    onChange(option);
    if (!multiSelect) setIsOpen(false);
  };

  const sortBySelectedResults = (results: any[]) => {
    const notActiveResults: any[] = [];
    const activeResults: any[] = [];

    results.forEach((option) => {
      const isActive = isActiveOption?.(option);
      if (isActive) activeResults.push(option);
      else notActiveResults.push(option);
    });

    return activeResults.concat(notActiveResults);
  };

  const removeRepeatedResults = (results: any[]) => {
    const notRepeatedResults = results.filter(function (option, i) {
      return (
        results.findIndex(
          (curr) => curr[optionValue] === option[optionValue],
        ) === i
      );
    });
    if (multiSelect) return sortBySelectedResults(notRepeatedResults);
    return notRepeatedResults;
  };

  const handleScroll = (element: HTMLElement, parent: HTMLElement) => {
    const relRef = getRelativeRect(element, parent);
    const elHeight = element.clientHeight;
    const top = relRef.top + parent.scrollTop!;
    const scrollPos = parent.scrollTop! + parent.clientHeight;
    if (top + elHeight > scrollPos)
      parent.scrollTop = top + elHeight - parent.clientHeight;
    if (top < parent.scrollTop) parent.scrollTop = top;
  };

  const handleKeyPress = ({ key }: KeyboardEvent<HTMLInputElement>) => {
    const list = partnerListRef.current;
    if (key === "Escape") setIsOpen(false);
    else setIsOpen(true);
    if (key === "Enter" && isOpened) {
      const currResult = result[selectedIndex];
      if (currResult) {
        selectOption(currResult);
        input.current?.blur();
        setTimeout(() => input.current?.focus(), 0);
      }
    } else if (key === "ArrowUp" && isOpened) {
      const newCurrIndex = selectedIndex - 1;
      const currResult = result[newCurrIndex];
      if (!currResult) {
        const selectedHtml = list!.children[
          result.length - 1
        ] as HTMLDivElement;
        handleScroll(selectedHtml, list!);
        setSelectedIndex(result.length - 1);
        if (clearInput) {
          input.current!.value = "";
        }
      } else {
        const selectedHtml = list!.children[newCurrIndex] as HTMLDivElement;
        handleScroll(selectedHtml, list!);
        setSelectedIndex(newCurrIndex);
        if (clearInput) {
          input.current!.value = "";
        }
      }
    } else if (key === "ArrowDown" && isOpened) {
      const newCurrIndex = selectedIndex + 1;
      const currResult = result[newCurrIndex];
      if (!currResult) {
        const selectedHtml = list!.children[0] as HTMLDivElement;
        handleScroll(selectedHtml, list!);
        setSelectedIndex(0);
        if (clearInput) {
          input.current!.value = "";
        }
      } else {
        const selectedHtml = list!.children[newCurrIndex] as HTMLDivElement;
        handleScroll(selectedHtml, list!);
        setSelectedIndex(newCurrIndex);
        if (clearInput) {
          input.current!.value = "";
        }
      }
    }
  };

  useEffect(() => {
    if (openOnFocus && !isOpen)
      setResult(resetOption ? [selecioneOption, ...options] : options);
    if (input.current) input.current.value = value;
  }, [options]);

  useEffect(() => {
    if (partnerListRef.current && input.current) {
      const target = partnerListRef.current;
      const inputWidth = input.current.clientWidth;
      target.style.minWidth = `${inputWidth}px`;
    }
  }, [isOpened]);

  const onSearch = async ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(value);
    if (value === "" && selectedOption) {
      selectOption(selecioneOption);
    }
    if (value) {
      const limit = openOnFocus ? options.length : 20;
      const result = filterArrayBy(options, value, searchKeys, sortBy).slice(
        0,
        limit,
      );
      setResult(resetOption ? [selecioneOption, ...result] : result);
    } else if (openOnFocus) {
      if (resetOption) {
        setResult([selecioneOption, ...options]);
      } else setResult(options);
    } else {
      setResult([]);
    }
    setSelectedIndex(-1);
  };

  const onClickSelect = (e: any, option: any, index: number) => {
    e.stopPropagation();
    selectOption(option);
    setSelectedIndex(index);
    if (selectedFirst && !isOpen)
      setResult((results) => removeRepeatedResults([option, ...results]));

    if (clearInput) {
      input.current!.value = "";
    }
    if (!multiSelect) e.currentTarget.focus();
    // if (value === "") {
    //   setSearchValue(option.name);
    // } else {
    //   setSearchValue(`${value}, ${option.name}`);
    // }
  };

  // Reflete o valor selecionado assim que ele muda, inclusive com o dropdown
  // aberto, para a opção clicada aparecer no filtro imediatamente.
  useEffect(() => {
    if (value || value === "") setSearchValue(value);
  }, [value]);

  // Limpa o debounce do modo filtro ao desmontar o componente
  useEffect(() => {
    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
        filterDebounceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (selectedFirst) setResult((results) => removeRepeatedResults(results));
  }, [isOpen]);

  const containerRef = useRef<HTMLDivElement>(null);

  const isClickOutsidePortal = (event: MouseEvent) => {
    if (
      containerRef.current &&
      containerRef.current.contains(event.target as Node)
    )
      return false;
    if (portalRef.current && portalRef.current.contains(event.target as Node))
      return false;
    return true;
  };

  createEventListener(
    "onClickBody",
    (event) => {
      if (isClickOutsidePortal(event)) {
        if (!disableOnClickSelect) {
          setIsOpen(false);
          setSearchValue(value);
        }
      }
    },
    id,
  );

  // Fecha este input quando um CollapsibleFilterGroup que o contém colapsa.
  // As opções são renderizadas em document.body (portal), então fechar o grupo
  // não as desmonta automaticamente — este listener garante o fechamento.
  createEventListener(
    "closePortalsInside",
    (root) => {
      if (
        isOpen &&
        root &&
        containerRef.current &&
        root.contains(containerRef.current)
      ) {
        setIsOpen(false);
        setSearchValue(value);
      }
    },
    `${id}-close-portals-inside`,
  );

  // Atualiza a posição do portal quando o dropdown abre
  useEffect(() => {
    if (isOpened) {
      const updatePosition = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          setPortalPosition({
            top: rect.bottom + 2,
            left: rect.left,
            width: rect.width,
          });
        }
      };
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isOpened]);

  // Registra o portal aberto para que listeners de "clique fora"
  // (ex.: CollapsibleFilterGroup) reconheçam cliques dentro deste portal
  useEffect(() => {
    if (!isOpened) return;
    registerPortal(id, {
      anchor: containerRef.current,
      content: partnerListRef.current,
    });
    return () => unregisterPortal(id);
  }, [isOpened, id]);

  return (
    <Box w="100%" gap="16px" {...mainBoxProps} ref={containerRef}>
      <Box w="100%" pos="relative">
        <InputFieldWithError
          ref={input}
          onChange={onSearch}
          value={searchValue}
          inputProps={{
            onKeyDown: handleKeyPress,
            isDisabled: isLoading,
            ...inputProps,
            _focus: {
              cursor: "auto",
            },
            onFocus: (e) => {
              setIsOpen(true);
              inputProps?.onFocus?.(e);
              setResult(options);

              if (value && value !== "") {
                setSearchValue("");
              }
            },
            onBlur: (e) => {
              if (!disableOnClickSelect) {
                setIsOpen(false);
              }
              inputProps?.onBlur?.(e);
              // setSearchValue(value);
            },
            cursor: "pointer",
          }}
          errorMessage={errorMessage}
          isLoading={isLoading}
          leftIcon={leftIcon ?? null}
          leftIconProps={leftIconProps}
          rightIcon={
            isOpened ? (
              <ChevronUpIcon
                cursor={"pointer"}
                onClick={() => {
                  setIsOpen(false);
                  input.current?.blur();
                }}
                {...rightIconProps}
              />
            ) : (
              <ChevronDownIcon
                cursor={"pointer"}
                onClick={() => {
                  setIsOpen(true);
                  input.current?.focus();
                }}
                {...rightIconProps}
              />
            )
          }
        />

        {isOpened
          ? createPortal(
              <Box
                borderRadius="0.375rem !important"
                bg={bg}
                border={border}
                padding="0px"
                ref={partnerListRef}
                style={{
                  position: "fixed",
                  top: `${portalPosition.top}px`,
                  left: `${portalPosition.left}px`,
                }}
                zIndex={9999}
                overflow="auto"
                maxH={"300px"}
                w="auto"
                onMouseDown={(e) => e.preventDefault()}
                {...openBoxProps}
              >
                <Box ref={portalRef}>
                  {result.slice(0, limitRender).map((item, index) => {
                    let isInclude = isActiveOption(item);
                    if (isFilter) {
                      if (multiSelect) {
                        // Cada clique alterna o estado visual da opção
                        const pendingCount = pendingOptions.filter(
                          (pending) => pending === item,
                        ).length;
                        if (pendingCount % 2 === 1) isInclude = !isInclude;
                      } else if (pendingOptions.length > 0) {
                        // Single select: destaca apenas a última seleção pendente
                        isInclude =
                          pendingOptions[pendingOptions.length - 1] === item;
                      }
                    }
                    const isSelected = index === selectedIndex;
                    return (
                      <Flex
                        p="5px 10px"
                        minH="30px"
                        h="30px"
                        key={`input-search-${index}`}
                        w="100%"
                        whiteSpace="nowrap"
                        _hover={{ bg: hoverColor }}
                        bg={
                          isInclude
                            ? activeColor
                            : isSelected
                              ? hoverColor
                              : undefined
                        }
                        cursor="pointer"
                        onClick={(e) =>
                          !disableOnClickSelect
                            ? onClickSelect(e, item, index)
                            : null
                        }
                        textAlign="start"
                        alignItems="center"
                        as="button"
                        onMouseDown={(e) => {
                          if (multiSelect) e.preventDefault();
                        }}
                        {...optionsFlexProps}
                      >
                        {multiSelect ? (
                          <Checkbox
                            mr="8px"
                            isChecked={isInclude}
                            onClick={(e) => onClickSelect(e, item, index)}
                          />
                        ) : null}{" "}
                        <Flex
                          align="center"
                          gap="4px"
                          fontSize={"13px !important"}
                        >
                          {optionLabelRender?.(item) ?? `${item[optionLabel]}`}
                          {enableCopyOption ? (
                            <CopyButton text={item[optionLabel] ?? item.name} />
                          ) : null}
                        </Flex>
                      </Flex>
                    );
                  })}
                </Box>
              </Box>,
              document.body,
            )
          : null}
      </Box>
    </Box>
  );
}
